// Typed wrappers around `/api/staff/exhibitions/*` (F-B / F-C).
//
// Mirrors shared/src/api/users.ts (F-A). Wire format is camelCase per
// ADR-0020 — the backend Pydantic `_CamelInput` / `_CamelResponse`
// bases produce + accept camelCase JSON, aliased to snake_case
// internally. Inline types here (rather than `components["schemas"]`)
// because F-B ships before the next `regen-types.sh` pass; the types
// match the schemas exactly and will fold into the regenerated set
// once the backend OpenAPI snapshot is refreshed at closeout.

import type { AxiosError } from "axios";

import { ApiError, getApiClient } from "./client";
import type { ApiErrorEnvelope } from "./client";
import type { ListStatus } from "./listStatus";
import type { components } from "./types";
import { saveBlob } from "../lib/download";

export type ExhibitionState = "draft" | "published" | "archived" | "purged";

export interface Exhibition {
  id: string;
  code: string;
  titleTc: string;
  titleEn: string;
  titleSc: string;
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
  coverImageId: string | null;
  qrCodeUrl: string | null;
  ownerTeamId: string | null;
  ownerDepartmentId: string | null;
  state: ExhibitionState;
  archivedStateSnapshot: Record<string, string> | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface ExhibitionListItem {
  id: string;
  code: string;
  titleTc: string;
  titleEn: string;
  titleSc: string;
  coverImageId: string | null;
  qrCodeUrl: string | null;
  ownerTeamId: string | null;
  ownerDepartmentId: string | null;
  // Resolved server-side for the Ownership column (team/department reads
  // are admin-only, so editors can't look names up themselves).
  ownerTeamName?: string | null;
  ownerDepartmentName?: string | null;
  zoneCount: number;
  state: ExhibitionState;
  archivedStateSnapshot?: Record<string, string> | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface ExhibitionCreatePayload {
  titleTc: string;
  descriptionTc: string;
  titleEn?: string;
  titleSc?: string;
  descriptionEn?: string;
  descriptionSc?: string;
  ownerTeamId?: string | null;
  ownerDepartmentId?: string | null;
}

export interface ExhibitionUpdatePayload {
  titleTc?: string | null;
  descriptionTc?: string | null;
  titleEn?: string | null;
  titleSc?: string | null;
  descriptionEn?: string | null;
  descriptionSc?: string | null;
  ownerTeamId?: string | null;
  ownerDepartmentId?: string | null;
}

export interface ExhibitionTransitionRequest {
  to_state: ExhibitionState;
}

export interface ExhibitionDeleteResponse {
  message: string;
}

export interface ExhibitionImageUploadResponse {
  message: string;
  coverImageId: string;
}

export interface ExhibitionCodeRegenerationResponse {
  message: string;
  code: string;
}

export interface ArchiveSummary {
  exhibition: Exhibition;
  archivedCount: number;
  restoredCount: number;
  partialFailures: { kind: string; id: string; reason: string }[];
  skipped: { kind: string; id: string; reason: string }[];
}

// ── Staff routes ─────────────────────────────────────────────────────

export async function listExhibitions(
  options: { includeArchived?: boolean; status?: ListStatus } = {},
): Promise<ExhibitionListItem[]> {
  // `status` (the staff filter) takes precedence; the legacy include_archived
  // flag stays for parent-picker callers that haven't moved over.
  const params: Record<string, string | boolean> = {};
  if (options.status) params.status = options.status;
  else if (options.includeArchived) params.include_archived = true;
  const { data } = await getApiClient().get<ExhibitionListItem[]>(
    "/staff/exhibitions",
    { params },
  );
  return data;
}

export async function getStaffExhibition(
  exhibitionId: string,
): Promise<Exhibition> {
  const { data } = await getApiClient().get<Exhibition>(
    `/staff/exhibitions/${exhibitionId}`,
  );
  return data;
}

export async function createExhibition(
  payload: ExhibitionCreatePayload,
): Promise<Exhibition> {
  const { data } = await getApiClient().post<Exhibition>(
    "/staff/exhibitions",
    payload,
  );
  return data;
}

export async function updateExhibition(
  exhibitionId: string,
  payload: ExhibitionUpdatePayload,
): Promise<Exhibition> {
  const { data } = await getApiClient().patch<Exhibition>(
    `/staff/exhibitions/${exhibitionId}`,
    payload,
  );
  return data;
}

export async function deleteExhibition(
  exhibitionId: string,
): Promise<ExhibitionDeleteResponse> {
  const { data } = await getApiClient().delete<ExhibitionDeleteResponse>(
    `/staff/exhibitions/${exhibitionId}`,
  );
  return data;
}

export async function transitionExhibition(
  exhibitionId: string,
  toState: ExhibitionState,
): Promise<Exhibition> {
  const { data } = await getApiClient().post<Exhibition>(
    `/staff/exhibitions/${exhibitionId}/transition`,
    { to_state: toState },
  );
  return data;
}

export async function uploadExhibitionImage(
  exhibitionId: string,
  file: File,
): Promise<ExhibitionImageUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await getApiClient().post<ExhibitionImageUploadResponse>(
    `/staff/exhibitions/${exhibitionId}/upload-image`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function regenerateExhibitionCode(
  exhibitionId: string,
): Promise<ExhibitionCodeRegenerationResponse> {
  const { data } =
    await getApiClient().post<ExhibitionCodeRegenerationResponse>(
      `/staff/exhibitions/${exhibitionId}/regenerate-code`,
    );
  return data;
}

export async function archiveExhibition(
  exhibitionId: string,
): Promise<ArchiveSummary> {
  const { data } = await getApiClient().post<ArchiveSummary>(
    `/staff/exhibitions/${exhibitionId}/archive`,
  );
  return data;
}

export async function restoreExhibition(
  exhibitionId: string,
): Promise<ArchiveSummary> {
  const { data } = await getApiClient().post<ArchiveSummary>(
    `/staff/exhibitions/${exhibitionId}/restore`,
  );
  return data;
}

export async function purgeExhibition(
  exhibitionId: string,
  reason: string,
): Promise<ArchiveSummary> {
  const { data } = await getApiClient().post<ArchiveSummary>(
    `/staff/exhibitions/${exhibitionId}/purge`,
    { reason },
  );
  return data;
}

// F-C: dedicated zones-in-exhibition fetch (returns ALL zones,
// regardless of state). The ExhibitionForm "Zones in this exhibition"
// fieldset uses this instead of filtering the role-aware /zones
// listing so archived + purged child zones stay visible in the
// historical record view.
//
// camelCase shape per ADR-0020 — the `/staff/exhibitions/{id}/zones` route returns
// the ZoneInExhibitionResponse projection (review #11, flipped to camel in #9).
// Typed here so the `.get()` isn't `any`, and consumers (useZonesInExhibition →
// qrBoard / ExhibitionQRBoard / ExhibitionForm) are drift-checked at the call site.
export interface ZoneInExhibition {
  id: string;
  code: string;
  titleTc: string;
  titleEn: string;
  titleSc: string;
  qrCodeUrl: string | null;
  coverImageId: string | null;
  exhibitIds: string[];
  exhibitionId: string;
  state: "draft" | "published" | "archived" | "purged";
}

export async function listZonesInExhibition(
  exhibitionId: string,
): Promise<ZoneInExhibition[]> {
  const { data } = await getApiClient().get<ZoneInExhibition[]>(
    `/staff/exhibitions/${exhibitionId}/zones`,
  );
  return data;
}

export async function exportExhibition(
  exhibitionId: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await getApiClient().post(
    `/staff/exhibitions/${exhibitionId}/export`,
    null,
    { responseType: "blob" },
  );
  // axios surfaces Content-Disposition only if exposed by CORS; fall
  // back to a sensible default if the header isn't readable.
  const cd = response.headers?.["content-disposition"] as string | undefined;
  let filename = `exhibition-${exhibitionId}.zip`;
  if (cd) {
    const match = /filename="?([^"]+)"?/.exec(cd);
    if (match && match[1]) filename = match[1];
  }
  return { blob: response.data as Blob, filename };
}

// ── F-N batch exhibit upload ─────────────────────────────────────────
// These use the generated types directly (the batch schemas are new and
// machine-shaped; no legacy inline-type debt to match).

export type BatchValidationReport =
  components["schemas"]["BatchValidationReport"];
export type BatchImportResult = components["schemas"]["BatchImportResult"];

function batchFormData(workbook: File, files: File[]): FormData {
  const fd = new FormData();
  fd.append("workbook", workbook);
  for (const f of files) fd.append("files", f);
  return fd;
}

/** With `responseType: "blob"` an error body arrives as a Blob, so the
 * client interceptor can't read the `{error:{...}}` envelope and the UI
 * would toast axios' generic "Request failed with status code N". Parse
 * the blob back into a proper ApiError so the real message surfaces. */
async function rehydrateBlobError(err: unknown): Promise<unknown> {
  const axiosErr =
    err instanceof ApiError ? err.cause : (err as AxiosError | undefined);
  const data = axiosErr?.response?.data;
  if (data instanceof Blob) {
    try {
      const envelope = JSON.parse(await data.text()) as ApiErrorEnvelope;
      if (envelope?.error?.message && envelope.error.code) {
        return new ApiError(envelope, axiosErr?.response?.status, axiosErr);
      }
    } catch {
      // body wasn't the JSON envelope — keep the original error
    }
  }
  return err;
}

/** Download the per-exhibition workbook template (auth'd axios → blob;
 * plain fetch would drop the Bearer header). */
export async function downloadBatchTemplate(
  exhibitionId: string,
  exhibitionCode: string,
): Promise<void> {
  try {
    const response = await getApiClient().get(
      `/staff/exhibitions/${exhibitionId}/exhibits/batch-template`,
      { responseType: "blob" },
    );
    saveBlob(
      response.data as Blob,
      `exhibition-${exhibitionCode}-exhibits.xlsx`,
    );
  } catch (err) {
    throw await rehydrateBlobError(err);
  }
}

export async function batchValidateExhibits(
  exhibitionId: string,
  workbook: File,
  files: File[],
): Promise<BatchValidationReport> {
  const { data } = await getApiClient().post<BatchValidationReport>(
    `/staff/exhibitions/${exhibitionId}/exhibits/batch-validate`,
    batchFormData(workbook, files),
    // Override the client's application/json default — same as
    // uploadExhibitionImage; without this FastAPI 422s the form.
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

/** Download the user's workbook marked up for fixing: failing cells
 * red + an errors column per dirty sheet. Parse-inert — the fixed file
 * re-uploads as-is (2b/2c). */
export async function downloadAnnotatedWorkbook(
  exhibitionId: string,
  workbook: File,
  files: File[],
): Promise<void> {
  try {
    const response = await getApiClient().post(
      `/staff/exhibitions/${exhibitionId}/exhibits/batch-annotate`,
      batchFormData(workbook, files),
      {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    const base = workbook.name.replace(/\.(xlsx|zip)$/i, "");
    saveBlob(response.data as Blob, `${base}-errors.xlsx`);
  } catch (err) {
    throw await rehydrateBlobError(err);
  }
}

export async function batchImportExhibits(
  exhibitionId: string,
  workbook: File,
  files: File[],
  allowDuplicates = false,
): Promise<BatchImportResult> {
  const fd = batchFormData(workbook, files);
  if (allowDuplicates) fd.append("allow_duplicates", "true");
  const { data } = await getApiClient().post<BatchImportResult>(
    `/staff/exhibitions/${exhibitionId}/exhibits/batch-import`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

// Visitor exhibition fetches (`/exhibitions/{id}`, `/exhibitions/by-code/{code}`)
// have no wrapper yet — the visitor ExhibitionDetail.jsx still calls raw axios.
// Adding the typed visitor wrappers + rewiring that page is part of review #7
// (the .jsx -> .tsx typing pass), where tsc can verify the snake/camel reads.
